import { useEffect, useRef, useState } from "react";
import PrismaticBurst from "../components/PrismaticBurst";
import Logo from "../components/Logo";
import HoloCard from "../components/HoloCard";
import SEOHead from "../components/SEOHead";
import { functions } from "../lib/backend/functions";
import "./Landing.css";

/* A real micrographic mark from public/micrographics. The SVGs are black
   line art; on this dark theme they are inverted to read light. */
function Mark({ n, className = "" }) {
  return (
    <img
      src={`/micrographics/mg-${n}.svg`}
      alt=""
      aria-hidden="true"
      className={`mark ${className}`}
    />
  );
}

function useIsLite() {
  const [lite, setLite] = useState(() => {
    if (typeof window === "undefined") return true;
    return (
      window.matchMedia("(max-width: 800px)").matches ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  });

  useEffect(() => {
    const mqSize = window.matchMedia("(max-width: 800px)");
    const mqMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setLite(mqSize.matches || mqMotion.matches);
    mqSize.addEventListener("change", update);
    mqMotion.addEventListener("change", update);
    return () => {
      mqSize.removeEventListener("change", update);
      mqMotion.removeEventListener("change", update);
    };
  }, []);

  return lite;
}

function LoadingScreen({ done }) {
  return (
    <div className={`loading-screen${done ? " loading-screen--done" : ""}`}>
      <div className="loading-logo">
        <Logo size={28} className="loading-logo-icon" />
        <span>pikra</span>
      </div>
      <div className="loading-bar-track">
        <div className="loading-bar-fill" />
      </div>
    </div>
  );
}

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("revealed");
          io.unobserve(el);
        }
      },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useReveal();
  return (
    <div
      ref={ref}
      className={`reveal ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* Small code panel used in the hero and the integrations section. */
function CodeCard({ file, mark, children, className = "" }) {
  return (
    <div className={`code-card glass ${className}`}>
      <div className="code-card-head">
        {mark != null && <Mark n={mark} className="code-card-mark" />}
        <span className="code-card-file">{file}</span>
      </div>
      <pre className="code-card-body">
        <code>{children}</code>
      </pre>
    </div>
  );
}

// Animated ticket showcase for the hero.
const DEMO_TICKETS = [
  {
    label: "Festival de invierno",
    sublabel: "General · Madrid · 2026",
    gradient: "linear-gradient(145deg, #281c59, #1a0f3d 50%, #0d1b2a)",
  },
  {
    label: "Charla en directo",
    sublabel: "Acceso único · Online",
    gradient: "linear-gradient(145deg, #1d0c2e, #281c59 50%, #112a3a)",
  },
  {
    label: "Cata maridada",
    sublabel: "Plaza limitada · Valencia",
    gradient: "linear-gradient(145deg, #2a1505, #1a0f3d 50%, #0d1320)",
  },
];

function TicketDemo() {
  const [idx, setIdx] = useState(0);
  const len = DEMO_TICKETS.length;

  useEffect(() => {
    const interval = setInterval(() => {
      setIdx((current) => (current + 1) % len);
    }, 4200);
    return () => clearInterval(interval);
  }, [len]);

  return (
    <div className="ticket-demo" aria-label="Entrada de ejemplo">
      <div className="ticket-demo-stack">
        {DEMO_TICKETS.map((ticket, i) => (
          <div
            key={i}
            className={`ticket-demo-slot${i === idx ? " ticket-demo-slot--active" : ""}`}
            aria-hidden={i !== idx}
          >
            <HoloCard {...ticket} />
          </div>
        ))}
      </div>
      <div className="ticket-demo-dots" role="tablist" aria-label="Cambiar ejemplo">
        {DEMO_TICKETS.map((_, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={i === idx}
            className={`ticket-demo-dot${i === idx ? " ticket-demo-dot--active" : ""}`}
            onClick={() => setIdx(i)}
            aria-label={`Ver ejemplo ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

const VALUE_PROPS = [
  {
    mark: 100,
    title: "Cobras tú directo",
    desc: "El dinero llega directo a tu cuenta, sin pasar por una cuenta intermedia.",
  },
  {
    mark: 12,
    title: "Comisiones bajas",
    desc: "Te quedas con casi todo lo que vendes. Sin cuotas mensuales ni cargos sorpresa al final de mes.",
  },
  {
    mark: 50,
    title: "Branding del organizador",
    desc: "Tu logo, tus colores, tu tipografía. Tu evento, no el escaparate de otra plataforma.",
  },
  {
    mark: 60,
    title: "Validación con QR",
    desc: "Cada entrada lleva un QR único de un solo uso. Lo escaneas en puerta desde el móvil y queda marcado al instante.",
  },
];

const EMBED_SNIPPET = `<!-- 1. Carga el script una vez -->
<script src="https://cdn.pikra.io/embed.js" defer></script>

<!-- 2. Coloca el checkout donde quieras -->
<pikra-checkout
  event="winterfest-2026"
  tier="general"
  locale="es"
  theme="auto">
</pikra-checkout>`;

const MCP_SNIPPET = `{
  "mcpServers": {
    "pikra": {
      "command": "npx",
      "args": ["-y", "@pikra/mcp@latest"],
      "env": { "PIKRA_API_KEY": "pk_live_…" }
    }
  }
}`;

const WIDGET_POINTS = [
  "Web component <pikra-checkout> o botón flotante.",
  "Atributos: event, tier, locale, theme, quantity.",
  "Checkout en un modal: el comprador no sale de tu web.",
  "Webhooks de venta para sincronizar tu sistema.",
];

const MCP_POINTS = [
  "Herramientas: create_event, list_sales, get_revenue, validate_ticket, export_attendees.",
  "Compatible con clientes MCP como Claude, Cursor o el SDK.",
  "Auth por API key con scopes por evento (solo lectura o gestión).",
  "Mismos límites y permisos que tu cuenta — nada de accesos extra.",
];

const FAQ_ITEMS = [
  {
    q: "¿Cuándo recibo el dinero de las ventas?",
    a: "El dinero llega directo a tu cuenta y se transfiere a tu banco con el calendario habitual. Nosotros no retenemos fondos.",
  },
  {
    q: "¿Cuánto cuesta vender con Pikra?",
    a: "Empezar es gratis y nuestras comisiones son bajas, sin cuotas mensuales ni cargos sorpresa. Los eventos gratuitos no pagan comisión.",
  },
  {
    q: "¿Necesito una cuenta para cobrar?",
    a: "Sí. La primera vez te guiamos para crear o conectar tu cuenta de cobros. Es un trámite estándar para vender online en Europa.",
  },
  {
    q: "¿Cómo se validan las entradas en puerta?",
    a: "Cada ticket lleva un QR único de un solo uso. Lo escaneas desde el móvil con la página /validate; un ticket validado no se puede reusar.",
  },
  {
    q: "¿Puedo integrar Pikra en mi propia web?",
    a: "Sí. Con el widget embebible <pikra-checkout> la venta vive dentro de tu página, y con el conector MCP gestionas eventos y ventas desde tus herramientas. Ambos usan tu API key.",
  },
];

function FaqItem({ q, a, index }) {
  return (
    <details className="faq-item">
      <summary className="faq-question">
        <span className="faq-num">{String(index + 1).padStart(2, "0")}</span>
        <span className="faq-q">{q}</span>
        <span className="faq-marker" aria-hidden="true" />
      </summary>
      <div className="faq-answer">{a}</div>
    </details>
  );
}

function SectionLabel({ index, children }) {
  return (
    <span className="sec-label">
      [ {index} — {children} ]
    </span>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function DemoForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    org: "",
    message: "",
  });
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError("Dinos tu nombre.");
    if (!EMAIL_RE.test(form.email.trim()))
      return setError("Necesitamos un email válido para responderte.");
    setError(null);
    setSubmitting(true);

    try {
      const { error: fnError } = await functions.requestDemo({
        name: form.name.trim(),
        email: form.email.trim(),
        org: form.org.trim(),
        message: form.message.trim(),
      });
      if (fnError) throw fnError;
      setSent(true);
    } catch (err) {
      setError(
        "No hemos podido enviar tu solicitud. Inténtalo de nuevo en un momento."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="demo-form" onSubmit={handleSubmit} noValidate>
      <div className="demo-field">
        <label htmlFor="df-name">Nombre</label>
        <input
          id="df-name"
          type="text"
          value={form.name}
          onChange={update("name")}
          placeholder="Tu nombre"
          autoComplete="name"
        />
      </div>
      <div className="demo-field">
        <label htmlFor="df-email">Email</label>
        <input
          id="df-email"
          type="email"
          value={form.email}
          onChange={update("email")}
          placeholder="tucorreo@ejemplo.com"
          autoComplete="email"
        />
      </div>
      <div className="demo-field">
        <label htmlFor="df-org">Organización o evento</label>
        <input
          id="df-org"
          type="text"
          value={form.org}
          onChange={update("org")}
          placeholder="Festival, sala, asociación…"
          autoComplete="organization"
        />
      </div>
      <div className="demo-field demo-field--full">
        <label htmlFor="df-msg">¿Qué quieres organizar?</label>
        <textarea
          id="df-msg"
          rows={3}
          value={form.message}
          onChange={update("message")}
          placeholder="Cuéntanos brevemente tu evento (opcional)"
        />
      </div>

      {error && (
        <p className="demo-error" role="alert">
          {error}
        </p>
      )}
      {sent && !error && (
        <p className="demo-sent" role="status">
          ¡Gracias! Hemos recibido tu solicitud y te responderemos en breve.
        </p>
      )}

      <div className="demo-form-actions">
        <button
          type="submit"
          className="btn-pill btn-pill--solid"
          disabled={submitting || sent}
        >
          {submitting ? "Enviando…" : sent ? "Enviado" : "Solicitar demo"}
        </button>
        <span className="demo-hint">Te respondemos a tu email.</span>
      </div>
    </form>
  );
}

export default function Landing() {
  const isLite = useIsLite();
  const [pageReady, setPageReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setPageReady(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  const scrollTo = (id) => (event) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <SEOHead
        title={null}
        description="Vende tickets para tu evento con tu marca. Cobras directo, validas con QR e integras Pikra en tu web con widgets embebibles o en tus herramientas con un conector MCP."
      />
      <LoadingScreen done={pageReady} />

      {/* Original animated background, fixed behind everything. */}
      <div className={`prismatic-fixed${isLite ? " prismatic-fixed--lite" : ""}`}>
        {!isLite && (
          <div className="hero-chromatic">
            <PrismaticBurst
              colors={["#cabeff", "#91d0e0", "#ffca3a", "#281c59"]}
              speed={0.16}
              intensity={0.7}
              animationType="rotate3d"
            />
          </div>
        )}
      </div>

      <div className="ls">
        {/* ───────────── HERO ───────────── */}
        <section className="ls-hero">
          <div className="ls-hero-grid">
            <div className="ls-hero-text">
              <p className="sec-label sec-label--kicker">
                [ PIKRA · TICKETS PARA EVENTOS ]
              </p>
              <h1 className="ls-display">
                <span className="ls-display-1">Vende tickets</span>
                <span className="ls-display-1">para tu evento.</span>
                <span className="ls-display-2">Tu marca, tu audiencia.</span>
              </h1>
              <p className="ls-lead">
                El dinero llega directo a tu cuenta y validas en puerta con QR. Y
                lo integras donde ya trabajas: en tu web, con widgets embebibles,
                y en tus herramientas, con un conector MCP.
              </p>
              <div className="ls-actions">
                <a
                  href="#demo-form"
                  className="btn-pill btn-pill--solid"
                  onClick={scrollTo("demo-form")}
                >
                  Solicitar demo
                </a>
                <a
                  href="#integra"
                  className="btn-pill btn-pill--ghost"
                  onClick={scrollTo("integra")}
                >
                  Ver integraciones
                </a>
              </div>
              <p className="ls-micro">Empieza gratis · comisiones bajas</p>
            </div>

            <div className="ls-hero-visual">
              <TicketDemo />
            </div>
          </div>
        </section>

        {/* ───────────── 01 · POR QUÉ PIKRA ───────────── */}
        <section id="value" className="ls-section">
          <Reveal className="ls-head">
            <SectionLabel index="01">POR QUÉ PIKRA</SectionLabel>
            <h2 className="ls-h2">Cuatro cosas que nos importan</h2>
            <p className="ls-lead">
              No somos un marketplace. Somos la herramienta del organizador.
            </p>
          </Reveal>
          <div className="values">
            {VALUE_PROPS.map((item, i) => (
              <Reveal key={item.title} className="value-card glass" delay={i * 80}>
                <div className="value-top">
                  <span className="value-index">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <Mark n={item.mark} className="value-mark" />
                </div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ───────────── 02 · INTEGRA (widgets + MCP, técnico) ───────────── */}
        <section id="integra" className="ls-section">
          <Reveal className="ls-head">
            <SectionLabel index="02">INTEGRA</SectionLabel>
            <h2 className="ls-h2">Pikra encaja donde ya trabajas</h2>
            <p className="ls-lead">
              Lleva la venta a tu propia web o gobiérnala desde tus herramientas.
              Una API key, dos formas de conectar.
            </p>
          </Reveal>

          <div className="integra">
            <Reveal className="integra-card glass">
              <div className="value-top">
                <span className="value-index">01</span>
                <Mark n={50} className="value-mark" />
              </div>
              <h3>Widgets embebibles</h3>
              <p>
                Un web component que monta el checkout de Pikra dentro de tu
                página. Copias dos líneas y la venta ocurre sin redirigir a
                nadie, con tu marca y tu aforo en tiempo real.
              </p>
              <CodeCard file="embed.html">{EMBED_SNIPPET}</CodeCard>
              <ul className="integra-list">
                {WIDGET_POINTS.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </Reveal>

            <Reveal className="integra-card glass" delay={100}>
              <div className="value-top">
                <span className="value-index">02</span>
                <Mark n={130} className="value-mark" />
              </div>
              <h3>Conector MCP</h3>
              <p>
                El servidor MCP de Pikra expone tu cuenta a asistentes de IA por
                el Model Context Protocol. Lo añades a la config de tu cliente y
                creas eventos, consultas ventas o validas entradas con lenguaje
                natural.
              </p>
              <CodeCard file=".mcp.json">{MCP_SNIPPET}</CodeCard>
              <ul className="integra-list">
                {MCP_POINTS.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>

        {/* ───────────── 03 · DUDAS ───────────── */}
        <section id="faq" className="ls-section ls-faq">
          <Reveal className="ls-head">
            <SectionLabel index="03">DUDAS</SectionLabel>
            <h2 className="ls-h2">Preguntas frecuentes</h2>
            <p className="ls-lead">
              ¿Te queda alguna duda? Escríbenos y te ayudamos a ponerlo en
              marcha.
            </p>
          </Reveal>
          <Reveal className="faq-list" delay={100}>
            {FAQ_ITEMS.map((item, i) => (
              <FaqItem key={item.q} q={item.q} a={item.a} index={i} />
            ))}
          </Reveal>
        </section>

        {/* ───────────── 04 · SOLICITA UNA DEMO ───────────── */}
        <section id="demo-form" className="ls-section ls-demoform">
          <div className="demoform-grid">
            <Reveal className="demoform-intro">
              <SectionLabel index="04">SOLICITA UNA DEMO</SectionLabel>
              <h2 className="ls-h2">¿Tienes un evento en mente?</h2>
              <p className="ls-lead">
                Cuéntanos qué quieres organizar y te enseñamos Pikra funcionando
                con tu caso — incluidas las integraciones. Sin compromiso.
              </p>
            </Reveal>
            <Reveal className="demoform-card glass" delay={120}>
              <DemoForm />
            </Reveal>
          </div>
        </section>
      </div>
    </>
  );
}
